import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  Alert,
  Table,
  ProgressBar,
  Form,
  InputGroup,
} from 'react-bootstrap';
import {
  FaArrowLeft,
  FaDollarSign,
  FaChartLine,
  FaChartBar,
  FaCalculator,
  FaBuilding,
  FaUser,
  FaSync,
  FaInfoCircle,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaSearch,
  FaFilter,
  FaSortAmountDown,
  FaSortAmountDownAlt,
  FaFileAlt,
  FaClipboardCheck,
} from 'react-icons/fa';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const API_BASE = 'http://34.206.193.218:4001/api/admin';

const CostVarianceList = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [costVarianceData, setCostVarianceData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('task');
  const [sortOrder, setSortOrder] = useState('desc');
  
  const token = localStorage.getItem('adminToken') || localStorage.getItem('token');

  /* ================= FETCH COST VARIANCE DATA ================= */
  const fetchCostVariance = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API_BASE}/adminCostVariance/projectCostVariance/${projectId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        setCostVarianceData(res.data);
      } else {
        toast.error('Failed to load cost variance data');
      }
    } catch (error) {
      console.error('Fetch cost variance error:', error);
      toast.error('Failed to load cost variance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchCostVariance();
    }
  }, [projectId]);

  /* ================= FORMATTERS ================= */
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const formatVariance = (variance, bidAmount) => {
    if (bidAmount === 0) return 'N/A';
    const percentage = (variance / bidAmount) * 100;
    return `${formatCurrency(variance)} (${formatPercentage(percentage)})`;
  };

  const getVarianceColor = (variance) => {
    if (variance > 0) return 'text-success'; // Over budget
    if (variance < 0) return 'text-danger';  // Under budget
    return 'text-secondary'; // On budget
  };

  const getStatusBadge = (status, variance) => {
    switch (status) {
      case 'over_budget':
        return <Badge bg="danger" className="px-3 py-2">Over Budget</Badge>;
      case 'under_budget':
        return <Badge bg="success" className="px-3 py-2">Under Budget</Badge>;
      case 'on_budget':
        return <Badge bg="info" className="px-3 py-2">On Budget</Badge>;
      default:
        if (variance > 0) return <Badge bg="danger" className="px-3 py-2">Over Budget</Badge>;
        if (variance < 0) return <Badge bg="success" className="px-3 py-2">Under Budget</Badge>;
        return <Badge bg="info" className="px-3 py-2">On Budget</Badge>;
    }
  };

  const getVarianceIcon = (variance) => {
    if (variance > 0) return <FaTimesCircle className="text-danger" />;
    if (variance < 0) return <FaCheckCircle className="text-success" />;
    return <FaInfoCircle className="text-info" />;
  };

  /* ================= FILTER & SORT ================= */
  const filteredTasks = costVarianceData?.tasks
    ?.filter(task => {
      // Filter by status
      if (filterStatus !== 'all') {
        const taskStatus = task.status || (task.variance > 0 ? 'over_budget' : task.variance < 0 ? 'under_budget' : 'on_budget');
        if (filterStatus !== taskStatus) return false;
      }
      
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          task.taskName?.toString().toLowerCase().includes(searchLower) ||
          task.taskId?.toLowerCase().includes(searchLower)
        );
      }
      
      return true;
    })
    .sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'task':
          aVal = a.taskName?.toString() || '';
          bVal = b.taskName?.toString() || '';
          break;
        case 'bid':
          aVal = a.bidAmount || 0;
          bVal = b.bidAmount || 0;
          break;
        case 'actual':
          aVal = a.actualCost || 0;
          bVal = b.actualCost || 0;
          break;
        case 'variance':
          aVal = Math.abs(a.variance || 0);
          bVal = Math.abs(b.variance || 0);
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        default:
          aVal = a.taskName?.toString() || '';
          bVal = b.taskName?.toString() || '';
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    }) || [];

  /* ================= CALCULATIONS ================= */
  const calculateOverallVariance = () => {
    if (!costVarianceData?.totals) return 0;
    const { totalBid, totalActual } = costVarianceData.totals;
    if (totalBid === 0) return 0;
    return ((totalActual - totalBid) / totalBid) * 100;
  };

  const calculateBudgetHealth = () => {
    const overallVariance = calculateOverallVariance();
    if (overallVariance > 10) return 'danger'; // Over budget by more than 10%
    if (overallVariance > 5) return 'warning'; // Over budget by 5-10%
    if (overallVariance > -5) return 'success'; // Within 5% budget
    return 'info'; // Under budget by more than 5%
  };

  const getBudgetHealthText = () => {
    const overallVariance = calculateOverallVariance();
    if (overallVariance > 10) return 'Significantly Over Budget';
    if (overallVariance > 5) return 'Slightly Over Budget';
    if (overallVariance > -5) return 'Within Budget';
    return 'Under Budget';
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading cost variance data...</span>
      </div>
    );
  }

  const overallVariance = calculateOverallVariance();
  const budgetHealth = calculateBudgetHealth();
  const budgetHealthText = getBudgetHealthText();

  return (
    <Container fluid className="py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* HEADER */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <Button
                variant="outline-primary"
                onClick={() => navigate(-1)}
                className="mb-3 d-flex align-items-center"
              >
                <FaArrowLeft className="me-2" />
                Back to Projects List
              </Button>
              <h2 className="fw-bold mb-2">
                <FaChartLine className="me-3" />
                Cost Variance Analysis
              </h2>
              
              {costVarianceData?.project && (
                <div className="mb-3">
                  <h4 className="mb-1">
                    <FaBuilding className="me-2 text-primary" />
                    {costVarianceData.project.name}
                  </h4>
                  <div className="d-flex flex-wrap gap-3 align-items-center">
                    <Badge bg="info" className="px-3 py-2">
                      <FaBuilding className="me-2" />
                      {costVarianceData.project.location}
                    </Badge>
                    <Badge bg="light" text="dark" className="px-3 py-2">
                      <FaUser className="me-2" />
                      {costVarianceData.project.user?.name}
                    </Badge>
                    <Badge bg="secondary" className="px-3 py-2">
                      <FaFileAlt className="me-2" />
                      Project ID: {projectId?.substring(0, 12)}...
                    </Badge>
                  </div>
                </div>
              )}

              <div className="d-flex align-items-center">
                <div className="me-4">
                  <span className="text-muted me-2">Budget Health:</span>
                  <Badge bg={budgetHealth} className="px-3 py-2">
                    {budgetHealthText}
                  </Badge>
                </div>
                <div>
                  <span className="text-muted me-2">Tasks:</span>
                  <Badge bg="dark">{costVarianceData?.tasks?.length || 0}</Badge>
                </div>
              </div>
            </div>
            
            <div className="d-flex gap-2">
              <Button
                variant="primary"
                onClick={fetchCostVariance}
                className="d-flex align-items-center"
              >
                <FaSync className="me-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* OVERALL BUDGET HEALTH */}
          <div className="mb-4">
            <div className="d-flex justify-content-between mb-1">
              <small className="text-muted">Overall Budget Variance</small>
              <small className={`fw-bold ${getVarianceColor(overallVariance)}`}>
                {formatPercentage(overallVariance)}
              </small>
            </div>
            <ProgressBar 
              now={Math.min(Math.abs(overallVariance), 100)} 
              variant={budgetHealth}
              style={{ height: '8px' }}
            />
          </div>
        </Card.Body>
      </Card>

      {/* SUMMARY CARDS */}
      {costVarianceData?.totals && (
        <Row className="g-3 mb-4">
          <Col md={4}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="text-center">
                <div className="mb-2">
                  <FaCalculator size={24} className="text-primary" />
                </div>
                <div className="fw-bold fs-4 text-primary">
                  {formatCurrency(costVarianceData.totals.totalBid)}
                </div>
                <small className="text-muted">Total Bid Amount</small>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={4}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="text-center">
                <div className="mb-2">
                  <FaDollarSign size={24} className="text-info" />
                </div>
                <div className="fw-bold fs-4 text-info">
                  {formatCurrency(costVarianceData.totals.totalActual)}
                </div>
                <small className="text-muted">Total Actual Cost</small>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={4}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="text-center">
                <div className="mb-2">
                  {getVarianceIcon(costVarianceData.totals.totalVariance)}
                </div>
                <div className={`fw-bold fs-4 ${getVarianceColor(costVarianceData.totals.totalVariance)}`}>
                  {formatCurrency(costVarianceData.totals.totalVariance)}
                </div>
                <small className="text-muted">Total Variance</small>
                <div className="mt-2 small">
                  <Badge bg={budgetHealth} className="px-2 py-1">
                    {budgetHealthText}
                  </Badge>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* FILTERS */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search tasks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setSearchTerm('')}
                  >
                    Clear
                  </Button>
                )}
              </InputGroup>
            </Col>
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <FaFilter />
                </InputGroup.Text>
                <Form.Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="over_budget">Over Budget</option>
                  <option value="under_budget">Under Budget</option>
                  <option value="on_budget">On Budget</option>
                </Form.Select>
              </InputGroup>
            </Col>
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>Sort by</InputGroup.Text>
                <Form.Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="task">Task Name</option>
                  <option value="bid">Bid Amount</option>
                  <option value="actual">Actual Cost</option>
                  <option value="variance">Variance</option>
                  <option value="status">Status</option>
                </Form.Select>
                <Button
                  variant={sortOrder === 'asc' ? 'primary' : 'outline-primary'}
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                >
                  {sortOrder === 'asc' ? <FaSortAmountDown /> : <FaSortAmountDownAlt />}
                </Button>
              </InputGroup>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* TASKS TABLE */}
      {filteredTasks.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <FaChartBar size={64} className="text-muted mb-3" />
            <h4 className="mb-3">No Tasks Found</h4>
            <p className="text-muted mb-4">
              {searchTerm || filterStatus !== 'all'
                ? 'No tasks match your search criteria'
                : 'No cost variance data available for this project.'}
            </p>
            {(searchTerm || filterStatus !== 'all') && (
              <Button 
                variant="outline-primary" 
                onClick={() => {
                  setSearchTerm('');
                  setFilterStatus('all');
                }}
              >
                Clear Filters
              </Button>
            )}
          </Card.Body>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>Task</th>
                    <th>Bid Amount</th>
                    <th>Actual Cost</th>
                    <th>Variance</th>
                    <th>Status</th>
                    
                  </tr>
                </thead>
                <tbody>
                  {filteredTasks.map((task, index) => {
                    const variancePercentage = task.bidAmount > 0 
                      ? (task.variance / task.bidAmount) * 100 
                      : 0;
                    
                    return (
                      <tr key={task.taskId}>
                        <td>
                          <Badge bg="light" text="dark" className="px-2 py-1">
                            {index + 1}
                          </Badge>
                        </td>
                        <td>
                          <div className="fw-bold">
                            {task.taskName || `Task ${index + 1}`}
                          </div>
                          <small className="text-muted">
                            ID: {task.taskId}
                          </small>
                        </td>
                        <td>
                          <div className="fw-bold text-primary">
                            {formatCurrency(task.bidAmount)}
                          </div>
                        </td>
                        <td>
                          <div className={`fw-bold ${task.actualCost > 0 ? 'text-info' : 'text-muted'}`}>
                            {formatCurrency(task.actualCost)}
                          </div>
                          <small className="text-muted">
                            {task.actualCost > 0 ? 'Actual' : 'Not Set'}
                          </small>
                        </td>
                        <td>
                          <div className={`fw-bold ${getVarianceColor(task.variance)}`}>
                            {formatCurrency(task.variance)}
                          </div>
                          <div className="small text-muted">
                            {formatPercentage(variancePercentage)}
                          </div>
                          <ProgressBar 
                            now={Math.min(Math.abs(variancePercentage), 100)} 
                            variant={task.variance > 0 ? 'danger' : 'success'}
                            style={{ height: '4px', marginTop: '4px' }}
                          />
                        </td>
                        <td>
                          {getStatusBadge(task.status, task.variance)}
                        </td>
                        
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
            
            {/* SUMMARY */}
            <div className="p-3 bg-light border-top">
              <Row>
                <Col md={4}>
                  <div className="small text-muted">
                    Showing {filteredTasks.length} of {costVarianceData?.tasks?.length || 0} tasks
                    {searchTerm && ` matching "${searchTerm}"`}
                  </div>
                </Col>
                <Col md={8} className="text-end">
                  <div className="d-flex justify-content-end gap-4">
                    <div className="text-center">
                      <div className="fw-bold text-primary">
                        {formatCurrency(costVarianceData?.totals?.totalBid || 0)}
                      </div>
                      <small className="text-muted">Total Bid</small>
                    </div>
                    <div className="text-center">
                      <div className="fw-bold text-info">
                        {formatCurrency(costVarianceData?.totals?.totalActual || 0)}
                      </div>
                      <small className="text-muted">Total Actual</small>
                    </div>
                    <div className="text-center">
                      <div className={`fw-bold ${getVarianceColor(costVarianceData?.totals?.totalVariance || 0)}`}>
                        {formatCurrency(costVarianceData?.totals?.totalVariance || 0)}
                      </div>
                      <small className="text-muted">Total Variance</small>
                    </div>
                    <div className="text-center">
                      <div className={`fw-bold ${budgetHealth}`}>
                        {formatPercentage(overallVariance)}
                      </div>
                      <small className="text-muted">Overall Variance</small>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </Card.Body>
        </Card>
      )}


      
    </Container>
  );
};

export default CostVarianceList;